import { useState } from "react";
import { Box, Button, Chip, Link, Typography } from "@mui/material";
import { formatCfdiUse, isCfdiUseValidForRegime } from "hvp-shared";
import { CfdiUseSelect } from "./CfdiUseSelect";

interface Props {
  /** The use this invoice will carry. Starts as the account's own. */
  value: string;
  onChange: (value: string) => void;
  /** The account's configured use — what the invoice falls back to. */
  accountDefault?: string;
  fiscalRegime?: string;
  rfc?: string;
  /** Where a permanent change is made, in the reader's own terms. */
  settingsHint: string;
  loading?: boolean;
}

/**
 * The CFDI use of a single invoice, presented as an exception rather than a question.
 *
 * A preselected dropdown asks something on every invoice, and does not say whether the
 * answer is already settled or still owed — so the person filling the form cannot tell
 * a default from a decision (Javier, 2026-09-20). Collapsed, this states the use and
 * where it comes from; changing it takes a deliberate click and says out loud that it
 * covers this invoice only.
 *
 * The account's own use is configured with `CfdiUseSelect`, which is the screen where a
 * dropdown *is* the right thing.
 */
export const CfdiUseForInvoice = ({
  value,
  onChange,
  accountDefault,
  fiscalRegime,
  rfc,
  settingsHint,
  loading,
}: Props) => {
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <Typography variant="body2" color="textSecondary" sx={{ my: 1.5 }}>
        Cargando los datos fiscales…
      </Typography>
    );
  }

  const fallback = accountDefault || "";
  // A stored use the regime does not admit is the one case that really is a decision
  // owed: the stamp would be refused. Force the field open instead of hiding it behind
  // a link nobody has a reason to click.
  const stale =
    !!value && !!fiscalRegime && !isCfdiUseValidForRegime(value, fiscalRegime, rfc);
  const changed = !!value && !!fallback && value !== fallback;
  // Nothing to state yet — the profile failed to load, or the account has no use.
  // Offer the field rather than an empty line pretending to say something.
  const expanded = open || stale || (!value && !fallback);

  if (!expanded) {
    return (
      <Box sx={{ my: 2 }}>
        <Typography variant="caption" color="textSecondary" display="block">
          Uso del CFDI
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="body2">{formatCfdiUse(value || fallback)}</Typography>
          {changed && <Chip size="small" color="warning" label="solo para esta factura" />}
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => setOpen(true)}
          >
            Cambiar para esta factura
          </Link>
        </Box>
        {!changed && (
          <Typography variant="caption" color="textSecondary">
            {settingsHint.charAt(0).toUpperCase() + settingsHint.slice(1)}.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ my: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        Uso del CFDI — solo para esta factura
      </Typography>

      <CfdiUseSelect
        value={value}
        onChange={onChange}
        fiscalRegime={fiscalRegime}
        rfc={rfc}
        label=""
        margin="none"
      />

      {stale && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
          El uso guardado no es válido con este régimen fiscal, así que hay que elegir otro
          para poder timbrar.
        </Typography>
      )}

      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
        Este cambio afecta únicamente a esta factura. Si todas deben salir con otro uso,
        {` ${settingsHint}`}.
      </Typography>

      {!stale && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => {
              onChange(fallback);
              setOpen(false);
            }}
          >
            {changed
              ? `Volver a ${formatCfdiUse(fallback)}`
              : "Dejarlo como está"}
          </Button>
        </Box>
      )}
    </Box>
  );
};
